import { FunctionComponent } from 'preact';
import { styled } from 'goober';
import { useConfig } from '../providers/ConfigProvider';
import { useUnifiedWidget } from '../providers/UnifiedWidgetProvider';
import { forwardRef } from 'preact/compat';
import CloseIcon from '../icons/Close';
import OpenIcon from '../icons/Open';

interface WidgetButtonProps {
  config: any;
  open: boolean;
}

const WidgetButtonStyled = styled('button', forwardRef)(({ config, open }: WidgetButtonProps) => ({
  display: "flex",
  flexShrink: 0,
  position: "relative",
  alignItems: "center",
  justifyContent: "center",
  gap: "5px",
  marginTop: "10px",
  cursor: "pointer",
  border: "none",
  height: `${config.buttonSize}px`,
  minWidth: `${config.buttonSize}px`,
  padding: `${config.buttonSize / 5}px`,
  borderRadius: `${config.buttonSize / 2}px`,
  color: config.buttonTextColor,
  fontSize: "16px",
  backgroundColor: config.primaryColor,
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.02)",
    backgroundColor: config.primaryColor,
  },
  "&:active": {
    transform: "scale(0.92)",
    transition: "all 0.2s ease",
    backgroundColor: config.primaryColor,
  },
  "&:focus-visible": {
    outline: "2px solid currentColor",
  },
  "&:focus:not(:focus-visible)": {
    outline: "none",
  },
  "& .icon": {
    position: "relative",
    height: `${config.buttonSize * 0.5}px`,
    width: `${config.buttonSize * 0.5}px`,
  },
  "& svg": {
    top: 0,
    left: 0,
    position: "absolute",
    width: "100%",
    height: "100%",
    transition: "all 0.2s ease-in-out",
  },
  ...( open ? {
    "svg.openSvg": {
      transform: "rotate(20deg) scale(0.5)",
      visibility: "hidden",
      opacity: 0,
    },
    "svg.closeSvg": {
      transition: "all .1s ease-in-out",
      opacity: 1,
    }
  } : {
    "svg.openSvg": {
      transition: "all .1s ease-in-out",
      opacity: 1,
    },
    "svg.closeSvg": {
      transform: "rotate(20deg) scale(0.5)",
      opacity: 0,
      visibility: "hidden",
    }
  }),
}));

export const WidgetButton: FunctionComponent = forwardRef<HTMLButtonElement>((_, ref) => {
  const { open, toggleWidget, initalised } = useUnifiedWidget();
  const { config } = useConfig();

  if (!initalised) {
    return null;
  }

  return (
    <WidgetButtonStyled 
      ref={ref} 
      onClick={toggleWidget} 
      open={open} 
      config={config} 
      aria-label={open ? "Close widget" : "Open widget"}
    >
      <div className="icon">
        <CloseIcon className="closeSvg" />
        <OpenIcon className="openSvg" />
      </div>
      {config.buttonText && <span style={{ paddingRight: "2px" }}>{ open ? config.closeButtonText || config.buttonText : config.buttonText }</span>}
    </WidgetButtonStyled>
  )
})

